import _ from 'lodash';

import { bindServices } from '@/services/api';
import { toPaginate } from '@/utils/infrastructure';
import { getFiltersGlobalFilters } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';
import { CoreEnums } from '@/utils/domain/constants';

const api = bindServices('connectionGroupConfig');
const { BulkActionType } = CoreEnums;

const namespace = 'connectionGroupConfig';
const fetchRoute = 'connectionGroup';
const fetchParams = 'connectionGroup';

const initialPagination = { page: 1, count: 0, totalPages: 0 };

const initialState = {
  list: [],
  pagination: initialPagination,
  orderBy: '',
  select: CoreQueries[fetchParams].paged.select,
  searchSelect: CoreQueries[fetchParams].paged.searchSelect,
  showForm: false,
  detail: false,
  id: null,
  item: null,
  success: false,
  validationErrors: [],
  showImport: false,
  importError: false,
  errorMessage: '',
  errors: [],
};

const ConnectionGroupModel = {
  namespace,
  state: initialState,

  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const pageSize = yield select((state) => state.global.pageSize);
        const params = {
          ...CoreQueries[fetchParams].paged,
          pageSize,
          ...payload,
        };
        const res = yield call(api.getAll, {
          route: `${fetchRoute}/paged`,
          params,
          readPriority: true,
        });
        yield put({
          type: 'queryData',
          payload: { ...res, limit: payload.limit ?? pageSize, page: payload.page ?? 1 },
        });
      } catch (e) {
        /*  */
      }
    },

    *fetchItem({ payload }, { call, put }) {
      try {
        yield put({ type: 'success', payload: false });
        const res = yield call(api.getById, {
          route: fetchRoute,
          id: payload.id,
          params: payload.detail
            ? CoreQueries[fetchParams]?.itemDetail
            : { ...CoreQueries[fetchParams]?.item, ...(payload?.params && { ...payload.params }) },
        });
        yield put({ type: 'queryItem', payload: res });
      } catch (e) {
        /*  */
      }
    },

    *fetchCreate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        yield call(api.create, { route: fetchRoute, data: payload.data });
        yield put({
          type: 'success',
          payload: { success: true },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchUpdate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        yield call(api.update, { route: fetchRoute, data: payload.data });
        yield put({
          type: 'success',
          payload: { success: true },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchDelete({ payload }, { call, put }) {
      try {
        const { id, view } = payload;
        yield put({
          type: 'global/modalConfirm',
          payload: { state: false, id: null },
        });
        if (view) {
          yield put({
            type: 'queryShowForm',
            payload: {
              state: false,
              item: null,
              detail: false,
              id: null,
            },
          });
        }
        yield call(api.remove, { route: fetchRoute, id });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *bulkDeleteConnectionGroups({ payload }, { call, put, select }) {
      try {
        const { ids } = payload;
        if (!ids || ids.length === 0) return;

        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Delete,
          ids,
        });

        const pageSize = yield select((state) => state.global.pageSize);
        const stateData = yield select((state) => state[namespace]);
        const globalState = yield select((state) => state.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const params = {
          ...CoreQueries[fetchParams].paged,
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(stateData.orderBy && { orderBy: stateData.orderBy }),
          ...(filters && { filter: filters }),
          select: stateData.select,
          ...(stateData.searchSelect && { searchSelect: stateData.searchSelect }),
        };

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'fetchList', payload: params });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *showForm({ payload }, { put }) {
      yield put({ type: 'queryShowForm', payload });
      if (!_.isNil(payload.id)) {
        yield put({ type: 'editOrDetail', payload });
      }
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'queryOrderBy', payload });
    },

    *setPaginationPage({ payload }, { put }) {
      yield put({ type: 'queryPaginationPage', payload });
    },

    *changeSuccess({ payload }, { put }) {
      yield put({ type: 'success', payload });
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },

    *toggleImportComponent({ payload }, { put }) {
      yield put({
        type: 'queryToggleImportComponent',
        payload: { showImport: payload.show },
      });
    },

    *fetchImport({ payload }, { call, put, select }) {
      try {
        const { file, isDeleteData } = payload;
        const pageSize = yield select((state) => state.global.pageSize);
        const stateData = yield select((state) => state[namespace]);
        const globalState = yield select((state) => state.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const params = {
          ...CoreQueries[fetchParams].paged,
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(stateData.orderBy && { orderBy: stateData.orderBy }),
          ...(filters && { filter: filters }),
          ...(stateData.searchSelect && { searchSelect: stateData.searchSelect }),
        };

        const res = yield call(api.apiRoute, {
          route: `${fetchRoute}/import`,
          method: 'post',
          data: { fileContent: file, deleteExistingData: isDeleteData },
        });

        if (res.errorMessage) {
          yield put({ type: 'queryImportErrors', payload: { ...res } });
        } else {
          yield put({ type: 'queryImport', payload: { ...res } });
          yield put({ type: 'fetchList', payload: params });
        }
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
            },
          });
        }
      }
    },

    *clearImportErrors(_, { put }) {
      yield put({ type: 'clearImportErrors' });
    },
  },

  reducers: {
    queryData(state, { payload }) {
      return {
        ...state,
        list: payload.items || [],
        pagination: _.merge(state.pagination, toPaginate(payload)),
      };
    },

    queryItem(state, { payload }) {
      return {
        ...state,
        item: payload,
      };
    },

    queryShowForm(state, { payload }) {
      return {
        ...state,
        showForm: payload.state,
        detail: payload.detail,
        id: payload.id,
        item: payload.item ?? null,
        success: false,
        validationErrors: [],
      };
    },

    editOrDetail(state, { payload }) {
      return {
        ...state,
        item: payload.item ?? {},
      };
    },

    queryOrderBy(state, { payload }) {
      return {
        ...state,
        orderBy: payload,
      };
    },

    queryPaginationPage(state, { payload }) {
      return {
        ...state,
        pagination: { ...state.pagination, page: payload },
      };
    },

    success(state, { payload }) {
      return {
        ...state,
        ...payload,
        success: payload?.success ?? payload,
        validationErrors: [],
      };
    },

    errors(state, { payload }) {
      return {
        ...state,
        validationErrors: payload.validationErrors || [],
        message: payload.message,
      };
    },

    queryToggleImportComponent(state, { payload }) {
      return {
        ...state,
        showImport: payload.showImport ?? payload.show,
      };
    },

    queryImport(state, { payload }) {
      return {
        ...state,
        showImport: false,
        importError: false,
        errorMessage: null,
        errors: null,
      };
    },

    queryImportErrors(state, { payload }) {
      return {
        ...state,
        importError: true,
        errorMessage: payload.errorMessage,
        errors: payload.errors || [],
      };
    },

    clearImportErrors(state) {
      return {
        ...state,
        errorMessage: null,
        errors: null,
        importError: false,
      };
    },

    reset() {
      return { ...initialState };
    },
  },
};

export default ConnectionGroupModel;
